import { Link } from "react-router-dom";
import { Clock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useChat } from "@/contexts/ChatContext";
import { formatDistanceToNow } from "date-fns";

interface InProgressPanelProps {
  recentlyViewed: Array<{
    product_id: string;
    created_at: string;
    products: {
      id: string;
      slug: string;
      title: string;
      image: string | null;
      price: number | null;
      rating: number | null;
    };
  }> | undefined;
  conversations: Array<{
    id: string;
    started_at: string;
    last_message_at: string;
    conversation_summary: string | null;
  }> | undefined;
}

export function InProgressPanel({ recentlyViewed, conversations }: InProgressPanelProps) {
  const { switchConversation } = useChat();

  const hasRecentActivity = (recentlyViewed && recentlyViewed.length > 0) || 
                           (conversations && conversations.length > 0);

  if (!hasRecentActivity) {
    return (
      <div className="border rounded-lg p-6 bg-card">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">In Progress</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Your recent activity and conversations
        </p>
        <div className="text-center py-8">
          <Clock className="h-12 w-12 mx-auto text-muted-foreground/30 mb-3" />
          <p className="text-sm text-muted-foreground">
            No recent activity yet. Start exploring tools or chat with AskCircle to get personalized recommendations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-card">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">In Progress</h2>
      </div>
      
      <p className="text-sm text-muted-foreground mb-4">
        Your recent activity and conversations
      </p>

      {/* Recently Viewed */}
      {recentlyViewed && recentlyViewed.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium mb-3">Recently Viewed</h3>
          <div className="space-y-2">
            {recentlyViewed.slice(0, 3).map((item) => (
              <Link
                key={item.product_id}
                to={`/product/${item.products.slug}`}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-accent transition-colors"
              >
                <img
                  src={item.products.image || '/placeholder.svg'}
                  alt={item.products.title}
                  className="h-10 w-10 rounded object-cover"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.products.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Recent Conversations */}
      {conversations && conversations.length > 0 && (
        <div>
          <h3 className="text-sm font-medium mb-3">Recent AskCircle Chats</h3>
          <div className="space-y-2">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                onClick={() => switchConversation(conv.id)}
                className="w-full flex items-start gap-3 p-2 rounded-lg hover:bg-accent transition-colors text-left"
              >
                <MessageCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm line-clamp-1">
                    {conv.conversation_summary || 'New conversation'}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatDistanceToNow(new Date(conv.last_message_at), { addSuffix: true })}
                  </p>
                </div>
              </button>
            ))}
          </div>
          
          <Link to="/ask-circle" className="block mt-3">
            <Button variant="outline" size="sm" className="w-full">
              View All Conversations
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}
